import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import api from '../utils/api';
import ProductCard from '../components/shared/ProductCard';
import { useSocket } from '../context/SocketContext';

const CATEGORIES = ['men', 'women'];
const SORTS = [
  { label: 'Newest Arrivals', value: 'newest' },
  { label: 'Top Rated', value: 'top_rated' },
  { label: 'Price: Low to High', value: 'price_asc' },
  { label: 'Price: High to Low', value: 'price_desc' },
];

export default function Shop() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const { socket } = useSocket();

  const category = searchParams.get('category') || 'all';
  const search = searchParams.get('search') || '';
  const sort = searchParams.get('sort') || 'newest';

  // State for controlled inputs to prevent cursor jumping while typing
  const [minPrice, setMinPrice] = useState(searchParams.get('minPrice') || '');
  const [maxPrice, setMaxPrice] = useState(searchParams.get('maxPrice') || '');

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (category !== 'all') params.set('category', category);
      if (search) params.set('search', search);
      if (sort) params.set('sort', sort);
      if (searchParams.get('minPrice')) params.set('minPrice', searchParams.get('minPrice'));
      if (searchParams.get('maxPrice')) params.set('maxPrice', searchParams.get('maxPrice'));
      params.set('page', page);
      params.set('limit', 12);

      const res = await api.get(`/products?${params.toString()}`);
      setProducts(res.data.products);
      setTotal(res.data.total);
      setPages(res.data.pages);
    } catch (err) {
      console.error("Failed to fetch products", err);
    }
    setLoading(false);
  }, [category, search, sort, searchParams, page]);

  useEffect(() => { fetchProducts(); }, [fetchProducts]);

  // Real-time updates via Socket.io
  useEffect(() => {
    if (!socket) return;
    const refresh = () => fetchProducts();
    socket.on('product:updated', refresh);
    socket.on('product:created', refresh);
    socket.on('product:deleted', refresh);
    return () => {
      socket.off('product:updated', refresh);
      socket.off('product:created', refresh);
      socket.off('product:deleted', refresh);
    };
  }, [socket, fetchProducts]);

  const updateFilter = (key, value) => {
    const p = new URLSearchParams(searchParams);
    if (value && value !== 'all') p.set(key, value);
    else p.delete(key);
    setSearchParams(p);
    setPage(1);
  };

  // Debounced/Effect-based price update to avoid excessive API calls
  useEffect(() => {
    const timer = setTimeout(() => {
      const p = new URLSearchParams(searchParams);
      if (minPrice) p.set('minPrice', minPrice); else p.delete('minPrice');
      if (maxPrice) p.set('maxPrice', maxPrice); else p.delete('maxPrice');
      setSearchParams(p);
    }, 500); // 500ms delay
    return () => clearTimeout(timer);
  }, [minPrice, maxPrice]);

  return (
    <div className="flex flex-col">
      {/* Cinematic Header */}
      <header className="relative h-[40vh] md:h-[50vh] flex items-center justify-center overflow-hidden bg-black">
        <video autoPlay muted loop playsInline className="absolute inset-0 w-full h-full object-cover opacity-60">
          <source src="75681-556671962_medium.mp4" type="video/mp4" />
        </video>
        <div className="relative z-10 text-center text-white px-4">
          <p className="text-[9px] md:text-[10px] uppercase tracking-[0.6em] mb-4 opacity-80">India Edition</p>
          <h1 className="font-display text-4xl md:text-7xl font-light tracking-tighter capitalize">
            {search ? `Results for "${search}"` : (category === 'all' ? 'Curated Collection' : category)}
          </h1>
          <p className="text-[10px] md:text-[11px] uppercase tracking-[0.2em] mt-6 opacity-60">
            {total} Handcrafted Pieces
          </p>
        </div>
      </header>

      <div className="px-6 md:px-12 lg:px-20 py-16">
        {/* Category Nav */}
        <nav className="flex justify-center gap-8 md:gap-12 overflow-x-auto pb-8 mb-16 border-b border-gray-100 no-scrollbar">
          {CATEGORIES.map(cat => (
            <button
              key={cat}
              onClick={() => updateFilter('category', cat)}
              className={`whitespace-nowrap text-[11px] uppercase tracking-[0.2em] pb-2 transition-all relative ${category === cat ? 'text-black font-semibold' : 'text-gray-400 hover:text-black'}`}
            >
              {cat}
              {category === cat && <div className="absolute bottom-0 left-0 w-full h-[1px] bg-black animate-slide-in" />}
            </button>
          ))}
        </nav>

        <div className="flex flex-col md:flex-row gap-16">
          {/* Sidebar */}
          <aside className="w-full md:w-48 flex-shrink-0 space-y-12">
            <div>
              <p className="text-[10px] uppercase tracking-[0.3em] font-bold text-black mb-6">Sort By</p>
              <div className="space-y-3">
                {SORTS.map(s => (
                  <button
                    key={s.value}
                    onClick={() => updateFilter('sort', s.value)}
                    className={`block w-full text-left text-[11px] tracking-wide transition-colors ${sort === s.value ? 'text-black font-bold' : 'text-gray-500 hover:text-black'}`}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <p className="text-[10px] uppercase tracking-[0.3em] font-bold text-black mb-6">Price Range (₹)</p>
              <div className="flex flex-col gap-4">
                <div className="relative">
                  <span className="absolute left-0 top-2 text-[10px] text-gray-400">₹</span>
                  <input
                    type="number"
                    placeholder="Min Price"
                    value={minPrice}
                    onChange={e => setMinPrice(e.target.value)}
                    className="w-full border-b border-gray-200 text-[11px] py-2 pl-4 bg-transparent focus:outline-none focus:border-black transition-colors"
                  />
                </div>
                <div className="relative">
                  <span className="absolute left-0 top-2 text-[10px] text-gray-400">₹</span>
                  <input
                    type="number"
                    placeholder="Max Price"
                    value={maxPrice}
                    onChange={e => setMaxPrice(e.target.value)}
                    className="w-full border-b border-gray-200 text-[11px] py-2 pl-4 bg-transparent focus:outline-none focus:border-black transition-colors"
                  />
                </div>
              </div>
            </div>
          </aside>

          {/* Main Grid */}
          <main className="flex-1">
            {loading ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-16">
                {[...Array(6)].map((_, i) => (
                  <div key={i} className="animate-pulse">
                    <div className="bg-gray-100 aspect-[3/4] mb-6" />
                    <div className="h-3 bg-gray-100 w-1/3 mb-3" />
                    <div className="h-4 bg-gray-100 w-3/4" />
                  </div>
                ))}
              </div>
            ) : products.length === 0 ? (
              <div className="text-center py-40 bg-gray-50">
                <p className="font-display text-2xl text-gray-400 italic">No pieces found in this selection.</p>
                <button
                  onClick={() => {
                    setSearchParams({});
                    setMinPrice('');
                    setMaxPrice('');
                  }}
                  className="mt-6 text-[10px] uppercase tracking-[0.3em] border-b border-black pb-1"
                >
                  Reset Filters
                </button>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-16">
                  {products.map(p => <ProductCard key={p._id} product={p} />)}
                </div>

                {pages > 1 && (
                  <div className="flex justify-center items-center gap-6 mt-24 pt-12 border-t border-gray-100">
                    {[...Array(pages)].map((_, i) => (
                      <button
                        key={i}
                        onClick={() => setPage(i + 1)}
                        className={`text-[11px] font-sans transition-all ${page === i + 1 ? 'text-black font-bold border-b-2 border-black' : 'text-gray-300 hover:text-black'}`}
                      >
                        0{i + 1}
                      </button>
                    ))}
                  </div>
                )}
              </>
            )}
          </main>
        </div>
      </div>
    </div>
  );
}